import type { CourseTree, CourseTreeSkill, CourseTreeUnit } from '../content/course.js';

/**
 * Shape of a completion, adapted from either:
 *   - the Supabase `user_lesson_progress` row (Phase 7+), or
 *   - an in-memory payload during an optimistic update.
 * The pure helper doesn't care where it came from; only that these
 * fields are present.
 */
export interface LessonCompletion {
  lessonId: string;
  xp: number;
  accuracy: number;
  perfect: boolean;
  completedAt: number;
}

/**
 * Pure progression logic — translates (course tree, completion map) →
 * per-lesson tree state. Kept out of the Zustand store so it's trivially
 * unit-testable and re-rendered only when its inputs change.
 *
 * Rules:
 *   - The very first lesson of the course is always unlocked.
 *   - Any other lesson is unlocked once its predecessor is complete.
 *   - Completed lessons show either `complete` or `golden` (perfect run).
 *   - The next-in-line unlocked lesson gets `in-progress` visual so the
 *     user knows where to tap (pairs with the `breathe` prop on LessonNode).
 *
 * A stricter "skill-gate" rule (complete whole skill → unlock next skill)
 * is NOT applied here — Duolingo uses lesson-by-lesson flow, which matches
 * what the PLAN wants. If we need stricter gating later we extend this.
 */

export type LessonNodeState = 'locked' | 'unlocked' | 'in-progress' | 'complete' | 'golden';

export interface TreeLessonNode {
  lessonId: string;
  skillId: string;
  unitId: string;
  title: string;
  titleEn: string;
  indexInCourse: number;
  state: LessonNodeState;
  exerciseCount: number;
}

export interface TreeSkillGroup {
  skill: CourseTreeSkill;
  lessons: TreeLessonNode[];
}

export interface TreeUnitGroup {
  unit: CourseTreeUnit;
  skills: TreeSkillGroup[];
}

export interface ProgressionView {
  units: TreeUnitGroup[];
  /** Id of the next unlocked-but-not-complete lesson (for scroll-into-view on home). */
  nextLessonId: string | null;
}

export function buildProgressionView(
  tree: CourseTree,
  completions: Record<string, LessonCompletion>,
): ProgressionView {
  const units: TreeUnitGroup[] = [];
  let nextLessonId: string | null = null;
  let prevComplete = true; // first lesson is always unlocked → treat "prev" as complete
  let indexInCourse = 0;

  for (const unit of tree.units) {
    const skillGroups: TreeSkillGroup[] = [];
    for (const skill of unit.resolvedSkills) {
      const nodes: TreeLessonNode[] = [];
      for (const lesson of skill.resolvedLessons) {
        const completion = completions[lesson.id];
        let state: LessonNodeState;
        if (completion) {
          state = completion.perfect ? 'golden' : 'complete';
        } else if (prevComplete) {
          state = 'in-progress';
          if (!nextLessonId) nextLessonId = lesson.id;
        } else {
          state = 'locked';
        }

        nodes.push({
          lessonId: lesson.id,
          skillId: skill.id,
          unitId: unit.id,
          title: lesson.title,
          titleEn: lesson.title_en,
          indexInCourse,
          state,
          exerciseCount: lesson.exercises.length,
        });

        prevComplete = Boolean(completion);
        indexInCourse++;
      }
      skillGroups.push({ skill, lessons: nodes });
    }
    units.push({ unit, skills: skillGroups });
  }

  // After the entire pass, normalize: the first "in-progress" stays as such;
  // any subsequent "in-progress" would be wrong (predecessor not complete).
  // The loop above only marks `in-progress` when `prevComplete`, so there's
  // only ever one in-progress node at most. Good.

  return { units, nextLessonId };
}

/** Is the given lesson id playable? Used by the LessonPlayer to reject deep links to locked content. */
export function isLessonUnlocked(
  lessonId: string,
  tree: CourseTree,
  completions: Record<string, LessonCompletion>,
): boolean {
  const view = buildProgressionView(tree, completions);
  for (const u of view.units) {
    for (const s of u.skills) {
      for (const n of s.lessons) {
        if (n.lessonId === lessonId) return n.state !== 'locked';
      }
    }
  }
  return false;
}
